import {
  WSButton,
  WSContainer,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSLoader
} from "@wingspanhq/fe-component-library";
import { useSession } from "@hooks/useSession";
import { useRestoreSession } from "@hooks/useRestoreSession";
import { analyticsService } from "@services/analyticsService";
import { useEffect } from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { LogoApp } from "../../../../shared/components/LogoApp";
import { Account } from "./Account";
import { History } from "./History";
import { RouteAddBankAccountManually } from "./RouteAddBankAccountManually";
import { RouteAddPaymentMethod } from "./RouteAddPaymentMethod";
import { RouteManagePaymentMethod } from "./RouteManagePaymentMethod";

export const ProtectedRoutes: React.FC<RouteComponentProps> = props => {
  const restoreSession = useRestoreSession();
  const session = useSession();

  // The client-payments app is its own shell — MemberApp's init never runs
  // here — so without this an authenticated payer browsing /payment/* stays
  // anonymous to Segment and PostHog. Keyed on the account id so it identifies
  // once per session, and re-identifies if the session is swapped.
  // Ported from wingspan-app@5033c3ecf (ARC-921), which identified from the v1
  // user + member profiles; v3 identifies by account id, as MemberApp does.
  useEffect(() => {
    if (session.accountId) {
      analyticsService.identify(session.accountId);
    }
  }, [session.accountId]);

  if (restoreSession.isLoading) {
    return (
      <Layout>
        <WSLoader.Spinner />
      </Layout>
    );
  }

  if (!session.personId) {
    const location = props.location;
    return (
      <Redirect to={`/member/sign-in#${location.pathname}${location.search}`} />
    );
  }

  return (
    <Layout>
      <Switch>
        <Route path="/payment/history" component={History} />
        <Route path="/payment/account" component={Account} />
        <Route
          path="/payment/add-bank-account-manually"
          component={RouteAddBankAccountManually}
        />

        <Route
          path="/payment/manage-payment-method"
          component={RouteManagePaymentMethod}
        />

        <Route
          path="/payment/add-payment-method"
          component={RouteAddPaymentMethod}
        />
      </Switch>
    </Layout>
  );
};

const Layout: React.FC = ({ children }) => {
  const history = useHistory();
  const session = useSession();
  const location = useLocation();

  return (
    <>
      <WSElement py="XL">
        <WSContainer>
          <WSFlexBox wrap="nowrap" justify="space-between" alignItems="center">
            <LogoApp />
            <WSFlexBox.CenterY>
              {location.pathname === "/payment/account" && (
                <WSButton.Link
                  size="S"
                  mr="XL"
                  onClick={() => {
                    history.push("/payment/history");
                  }}
                >
                  History
                </WSButton.Link>
              )}
              {location.pathname === "/payment/history" && (
                <WSButton.Link
                  size="S"
                  mr="XL"
                  onClick={() => {
                    history.push("/payment/account");
                  }}
                >
                  Settings
                </WSButton.Link>
              )}
              {!!session.personId && (
                <WSButton.Secondary
                  size="S"
                  onClick={() => {
                    history.push("/sign-out#/payment/history");
                  }}
                >
                  Sign out
                </WSButton.Secondary>
              )}
            </WSFlexBox.CenterY>
          </WSFlexBox>
        </WSContainer>
      </WSElement>
      <WSDivider />
      <WSElement py="3XL">
        <WSContainer>{children}</WSContainer>
      </WSElement>
    </>
  );
};
